import cn from 'classnames';
import spacetime from 'spacetime';
import { useField } from 'react-final-form';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { IKnmErknmFormViolation } from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { ViolationStatus } from 'KnmErknmPackage/ErknmViolationsModule/components/ViolationStatus';
import { ViolationStatusesEnum } from 'APIPackage/KnmModule/enums/ViolationStatusesEnum';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { IconQuestion } from 'UIPackage/IconsModule/components/IconQuestion';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { ErknmDecisionViolationFixPlanDatesField } from 'DecisionPackage/DecisionViolationModule/components/ErknmDecisionViolationFixPlanDatesField';
import { ErknmDecisionViolationFixPlanMethodsField } from 'DecisionPackage/DecisionViolationModule/components/ErknmDecisionViolationFixPlanMethodsField';
import { ErknmDecisionViolationFixPlanExplanationsField } from 'DecisionPackage/DecisionViolationModule/components/ErknmDecisionViolationFixPlanExplanationsField';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { ErknmDecisionViolationFixItems } from 'DecisionPackage/DecisionViolationModule/components/ErknmDecisionViolationFixItems';
import { ViolationFixGradesEnum } from 'APIPackage/KnmModule/enums/ViolationFixGradesEnum';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { IBooleanSelectOption } from 'KnmPackage/MainModule/interfaces/IBooleanSelectOption';
import { AgreementStatusEnum } from 'APIPackage/KnmModule/enums/AgreementStatusEnum';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { ViolationFixMethodsEnum } from 'APIPackage/KnmModule/enums/ViolationFixMethodsEnum';

import { KnmCollapse } from '../../MainModule/components/KnmCollapse';
import styles from '../../../KnmErknmPackage/ErknmViolationsModule/components/ErknmViolationItem.module.scss';
import { convertQuillDeltaToHtml } from '../../../functions/convertQuillDeltaToHtml';
import { IReactQuillDelta } from '../../../APIPackage/KnmModule/interfaces/IReactQuillDelta';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';
import { Hint } from '../../../CorePackage/MainModule/components/Hint';

interface IErknmDecisionViolationItemProps {
  violation: IKnmErknmFormViolation;
  fieldName: string;
  formRef: IKnmFormRef | undefined;
  onRemove: () => void;
  watchOnly: boolean;
  isPaperless: boolean;
  onChangeFixPlanDate?: (date: string | Date) => void;
  mId: string;
  isKnm?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
  signed: boolean;
  isResultDecisionSinged?: boolean;
  extractVersion?: number;
  isRepeatCheck?: boolean;
  isSupervisor?: boolean;
  resigningRequired?: boolean;
  isExpandAll?: boolean;
  violationsLength: number;
  isProductsResultDecision?: boolean;
}

const negativeSelectOption = booleanSelectOptions.find(option => !option.value);

export const ErknmDecisionViolationItem = ({
  violation,
  fieldName,
  formRef,
  onRemove,
  watchOnly,
  isPaperless,
  onChangeFixPlanDate,
  mId,
  isKnm,
  isPm,
  isDecision,
  signed,
  isResultDecisionSinged,
  extractVersion,
  isRepeatCheck,
  isSupervisor,
  resigningRequired,
  isExpandAll,
  violationsLength,
  isProductsResultDecision,
}: IErknmDecisionViolationItemProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  const [isShowFullText, setIsShowFullText] = useState(false);

  const isNotFixedByStatus: boolean = violation.status === ViolationStatusesEnum.NOT_FIXED;
  const isFixedByStatus: boolean = violation.status === ViolationStatusesEnum.FIXED;
  const isAppealedByStatus: boolean = violation.status === ViolationStatusesEnum.APPEALED;
  const isOnApproval: boolean = violation.status === ViolationStatusesEnum.APPROVING;
  const isSuspendByAgreement: boolean = violation.status === ViolationStatusesEnum.SUSPENDED_BY_AGREEMENT;
  const isAppealed: boolean = !!violation.isAppealed?.value;
  const hasNegativeGradeFixes: boolean = !!violation.fixes?.some(
    fix => fix.grade?.value === ViolationFixGradesEnum.NOT_CORRECT,
  );
  const hasMobileAppInfo: boolean = !!violation.fixes?.some(
    fix => fix.method?.value === ViolationFixMethodsEnum.MOBILE_APP_INFO,
  );

  const agreements: IKnmForm['agreements'] = useField('agreements' as keyof IKnmForm, { subscription: { value: true } })
    .input.value;

  const repeatCorrectedValue: IBooleanSelectOption = useField(`${fieldName}.repeatCorrected`, {
    subscription: { value: true },
  })?.input?.value;

  const repeatCorrectedInEventValue: IBooleanSelectOption = useField(`${fieldName}.repeatCorrectedInEvent`, {
    subscription: { value: true },
  })?.input?.value;

  const fixFactMethodInitialValue: IBooleanSelectOption = useField(`${fieldName}.fixFactMethod`, {
    subscription: { value: true },
  })?.input?.value;

  // @ts-ignore
  const reasons = formRef?.current?.getFieldState(isDecision ? `reasons` : `reasonsErknm`)?.value;
  const erpId = reasons?.[0]?.links?.[0]?.erpId;

  // Пояснение относится только к обжалованию, при снятии обжалования его очищаем
  const handleIsAppealedChange = useCallback(
    (v: IBooleanSelectOption) => {
      if (!v.value && violation.explanation) {
        formRef?.current.change(`${fieldName}.explanation` as keyof IKnmForm);
      }
    },
    [formRef, fieldName, violation.explanation],
  );

  const onChangeIsFixedOnPlace = useCallback(
    (v: IBooleanSelectOption) => {
      if (!v.value && violation.repeatExplanation) {
        // @ts-ignore
        formRef?.current?.change(`${fieldName}.repeatExplanation` as keyof IKnmForm);
      }
      if (v.value === true) {
        // @ts-ignore
        formRef?.current.change(`${fieldName}.includeInSubject`, false);
      }
    },
    [formRef, fieldName, violation.repeatExplanation],
  );

  // Обжалование возможно только при материале с отрицательной оценкой, без него снимаем обжалование и пояснение
  useEffect(() => {
    if (!hasNegativeGradeFixes && isAppealed) {
      formRef?.current.change(`${fieldName}.isAppealed` as keyof IKnmForm, negativeSelectOption);

      if (violation.explanation) {
        formRef?.current.change(`${fieldName}.explanation` as keyof IKnmForm);
      }
    }
  }, [hasNegativeGradeFixes, isAppealed, formRef, fieldName, violation.explanation]);

  // Устранено ли в ходе другого мероприятия
  const isFixedDuringAnotherEvent = fixFactMethodInitialValue?.id === 5;

  const getFilterFixFactMethodSelectOptions = useCallback(
    (arr: ISelectOption<string | number>[] | undefined) => {
      return arr
        ?.map(item => ({
          ...item,
          isDisabled: item.id === 1,
        }))
        .filter(e => !isFixedDuringAnotherEvent && e.id !== 5);
    },
    [isFixedDuringAnotherEvent],
  );

  const handleViolationResolvedChange = useCallback(
    (v: IBooleanSelectOption) => {
      if (isPaperless) {
        if (v.value === false) {
          // @ts-ignore
          formRef?.current.change(`${fieldName}.fixes`);
        }
        if (v.value === true) {
          // @ts-ignore
          formRef?.current.change(`${fieldName}.includeInSubject`, false);
        }
      }
    },
    [formRef, isPaperless],
  );

  const collapseBlockId = useMemo(() => {
    if (violation) {
      return `result-decisions-violation-${violation.id}`;
    }
  }, [violation]);

  const hasError = checkHasErrorInCollapseBlock(formRef, fieldName);

  /**
   * Связанное соглашение в статусе "Утверждено"
   */
  const bindedAgreement = useMemo(() => {
    if (!Array.isArray(agreements)) {
      return undefined;
    }

    const approvedAgreements = agreements?.filter(agreement => agreement.status === AgreementStatusEnum.APPROVED);

    if (!approvedAgreements || approvedAgreements.length === 0) {
      return undefined;
    }

    let agr: IErknmFormAgreement | undefined;

    approvedAgreements.forEach(agreement => {
      agreement.violations?.forEach(agrViolation => {
        agrViolation.violatedMandatory.forEach(vm => {
          vm.violations?.forEach(vi => {
            if (vi.id === violation.id) {
              agr = agreement;
            }
          });
        });
      });
    });

    return agr;
  }, [agreements, violation]);

  /**
   * Определение занятости нарушения в соглашениях
   */
  const isDisabledByAgreement = useMemo(() => {
    if (!Array.isArray(agreements)) {
      return false;
    }

    const availableAgreements = agreements?.filter(
      agreement =>
        agreement.status === AgreementStatusEnum.DRAFT ||
        agreement.status === AgreementStatusEnum.READY_TO_APPROVE ||
        agreement.status === AgreementStatusEnum.ON_APPROVAL ||
        agreement.status === AgreementStatusEnum.PENDING_TERMINATION ||
        agreement.status === AgreementStatusEnum.COMPLETED,
    );

    if (!availableAgreements || availableAgreements.length === 0) {
      return false;
    }

    let isDisabled = false;

    availableAgreements.forEach(agreement => {
      agreement.violations?.forEach(agrViolation => {
        agrViolation.violatedMandatory.forEach(vm => {
          vm.violations?.forEach(vi => {
            if (vi.id === violation.id) {
              isDisabled = true;
            }
          });
        });
      });
    });

    return isDisabled;
  }, [agreements, violation]);

  /**
   * Отображаемое поле для компонента "Нарушение устранено"
   */
  const isFixedFieldName = useMemo(() => {
    if (isRepeatCheck) {
      if (violation.repeatCorrected?.value) return 'repeatCorrected';

      if (violation.repeatCorrectedInEvent?.value) return 'repeatCorrectedInEvent';

      return 'repeatCorrected';
    }

    return 'isFixed';
  }, [violation, isRepeatCheck]);

  const hasViolationFixItems =
    violation.isFixed?.value ||
    !!violation.fixes?.length ||
    violation.repeatCorrected?.value ||
    violation.repeatCorrectedInEvent?.value;

  const { convertedHtml, fullHtml, isTruncated } = useMemo(() => {
    if (violation.descriptionReactQuillEditor && typeof violation.descriptionReactQuillEditor === 'object') {
      try {
        const html = convertQuillDeltaToHtml(violation.descriptionReactQuillEditor as IReactQuillDelta);
        const MAX_HTML_LENGTH = 2000;

        if (html.length > MAX_HTML_LENGTH) {
          return {
            convertedHtml: html.substring(0, MAX_HTML_LENGTH),
            fullHtml: html,
            isTruncated: true,
          };
        }

        return {
          convertedHtml: html,
          fullHtml: html,
          isTruncated: false,
        };
      } catch (error) {
        console.error('Error converting Quill delta to HTML:', error);

        return {
          convertedHtml: '',
          fullHtml: '',
          isTruncated: false,
        };
      }
    }

    return {
      convertedHtml: null,
      fullHtml: null,
      isTruncated: false,
    };
  }, [violation.descriptionReactQuillEditor]);

  const handleToggleFullText = useCallback(() => {
    setIsShowFullText(prev => !prev);
  }, []);

  /**
   * Компонент управления исправлением нарушения либо в первичной, либо во вторичной проверке
   */
  const EliminationComponents = useMemo(() => {
    return (
      <>
        <Row>
          <FieldBlock
            label={
              <Row align="center" margin="none" notSpace>
                <span>Нарушение устранено</span>
                <Tooltip
                  placement="right"
                  content={
                    <div className={styles.TooltipContent}>
                      Поле доступно для изменения после первого подписания предписания, а также если статус нарушения
                      имеет значение &quot;Не устранено&quot; или &quot;Ожидает подтверждения&quot;. Значение
                      &quot;Да&quot; в поле устанавливается в случае, если устранение произошло после проведения
                      мероприятия.
                    </div>
                  }
                >
                  <IconQuestion className={styles.TooltipIcon} />
                </Tooltip>
              </Row>
            }
            placementLabel="left"
            labelFontSize="small"
            color={isPaperless ? 'black' : undefined}
            className={cn({ [styles.FieldBlock]: isPaperless })}
            type="not-space"
          >
            <SelectInputField
              className={isPaperless ? '' : 'shared-row-field'}
              name={`${fieldName}.${isFixedFieldName}`}
              options={booleanSelectOptions}
              isClearable={false}
              defaultValue={negativeSelectOption}
              onChange={handleViolationResolvedChange}
              disabled={
                (bindedAgreement ? !isSuspendByAgreement : !isNotFixedByStatus) ||
                watchOnly ||
                violation.isNew ||
                (isPaperless && !signed) ||
                isDisabledByAgreement
              }
            />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock
            label={
              <Row align="center" margin="none" notSpace>
                <span>Нарушение обжаловано</span>
                <Tooltip
                  placement="right"
                  content={
                    <div className={styles.TooltipContent}>
                      Поле доступно для изменения, если в представленных материалах «Оценка исправления нарушения» имеет
                      значение «Не исправлено». Значение «Да» в поле устанавливается в случае, если нарушение было
                      обжаловано в пользу заявителя.
                    </div>
                  }
                >
                  <IconQuestion className={styles.TooltipIcon} />
                </Tooltip>
              </Row>
            }
            placementLabel="left"
            labelFontSize="small"
            color={isPaperless ? 'black' : undefined}
            className={cn({ [styles.FieldBlock]: isPaperless })}
            type="not-space"
          >
            <SelectInputField
              className={isPaperless ? '' : 'shared-row-field'}
              name={`${fieldName}.isAppealed`}
              options={booleanSelectOptions}
              isClearable={false}
              onChange={handleIsAppealedChange}
              disabled={
                (!hasNegativeGradeFixes && isNotFixedByStatus) ||
                isOnApproval ||
                isFixedByStatus ||
                isAppealedByStatus ||
                watchOnly ||
                isDisabledByAgreement
              }
              defaultValue={negativeSelectOption}
            />
          </FieldBlock>
        </Row>
        {isAppealed && (
          <Row>
            <FieldBlock
              label="Пояснение"
              labelFontSize="small"
              placementLabel="left"
              color={isPaperless ? 'black' : undefined}
              className={cn({ [styles.FieldBlock]: isPaperless })}
              type="not-space"
            >
              <TextareaField
                className={isPaperless ? '' : 'shared-row-field'}
                name={`${fieldName}.explanation`}
                placeholder="Введите пояснение (при необходимости)"
                disabled={isOnApproval || isAppealedByStatus || watchOnly || isDisabledByAgreement}
                autoHeight
              />
            </FieldBlock>
          </Row>
        )}
      </>
    );
  }, [
    isRepeatCheck,
    fieldName,
    isPm,
    isKnm,
    isDecision,
    erpId,
    violation,
    isPaperless,
    mId,
    handleViolationResolvedChange,
    onChangeIsFixedOnPlace,
    formRef,
    isFixedFieldName,
    isNotFixedByStatus,
    isOnApproval,
    watchOnly,
    signed,
    isDisabledByAgreement,
    hasNegativeGradeFixes,
    isFixedByStatus,
    isAppealedByStatus,
    isAppealed,
  ]);

  if (isProductsResultDecision) {
    return (
      <span className="shared-break-word">
        <KnmCollapse
          id={collapseBlockId}
          title={`Нарушение №${violation.number}: «${violation.title}»`}
          onClose={!watchOnly ? onRemove : undefined}
          isShowOpenButton={false}
        >
          <></>
        </KnmCollapse>
      </span>
    );
  }

  return (
    <KnmCollapse
      id={collapseBlockId}
      title={
        <span className="shared-break-word">
          <span>Нарушение №{violation.number}:</span>{' '}
          {convertedHtml ? (
            <>
              <div
                /* eslint-disable-next-line react/no-danger */
                dangerouslySetInnerHTML={{
                  __html: isShowFullText ? fullHtml : convertedHtml,
                }}
                className="ql-editor ql-editor-preview-block"
                style={{ whiteSpace: 'pre-wrap' }}
              />
              {isTruncated && (
                <span className={styles.ShowMoreButtonWrapper} onClick={e => e.stopPropagation()}>
                  <button
                    type="button"
                    className={styles.ShowMoreButton}
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleToggleFullText();
                    }}
                    onMouseDown={e => e.stopPropagation()}
                  >
                    {isShowFullText ? 'Скрыть' : 'Показать полностью'}
                  </button>
                </span>
              )}
            </>
          ) : (
            <>«{violation.title}»</>
          )}
        </span>
      }
      bodyClassName={styles.ItemCollapseBody}
      headerPostfix={
        <div className={styles.PostfixWrapper}>
          <ViolationStatus status={violation.status} />
          {violation.status !== ViolationStatusesEnum.FIXED &&
            (repeatCorrectedValue.value || repeatCorrectedInEventValue.value) &&
            resigningRequired && (
              <span className={styles.StatusNotificationGreen}>
                После подписания решения статус нарушения изменится на &rdquo;Устранено&rdquo;
              </span>
            )}
        </div>
      }
      onClose={violation.isNew ? onRemove : undefined}
      openByDefault={(violation.isNew || isExpandAll) && violationsLength <= 50}
      error={isKnmCollapseOpened && hasError}
      changeOpenedCallback={setIsKnmCollapseOpened}
    >
      <>
        {EliminationComponents}

        {!isProductsResultDecision && (
          <>
            <Row margin="none">
              <FieldBlock
                label="Сведения о плане по устранению нарушения"
                className={cn({ [styles.GroupMarginTop]: isPaperless })}
              />
            </Row>
            <Row margin="none">
              {isPaperless ? (
                <>
                  {bindedAgreement && (
                    <Row margin="none">
                      <FieldBlock
                        label={'Дата устранения по соглашению'}
                        placementLabel="left"
                        labelFontSize="small"
                        color={'black'}
                        className={styles.FieldBlock}
                        type="not-space"
                        margin="bottom"
                      >
                        <DatePickerField name={`${fieldName}.dateResolvedByAgreement`} disabled={true} />
                      </FieldBlock>
                    </Row>
                  )}
                  <Row>
                    <FieldBlock
                      label={getTitleFieldWithRedStar({
                        label: 'Плановая дата устранения',
                        field: `${fieldName}.fixPlanDates[0].value`,
                        isPm,
                        isKnm,
                        isDecision,
                      })}
                      placementLabel="left"
                      labelFontSize="small"
                      color={isPaperless ? 'black' : undefined}
                      className={cn({ [styles.FieldBlock]: isPaperless }, styles.PositionRelative)}
                      type="not-space"
                      id={`${mId}.fixPlanDates[0].value`}
                    >
                      {bindedAgreement && (
                        <div className={styles.FixPlanDatesHint}>
                          <Hint text="Дата приостановлена" />
                        </div>
                      )}
                      <DatePickerField
                        name={`${fieldName}.fixPlanDates[0].value`}
                        placeholder="Выберите дату и время"
                        disabled={
                          (!isNotFixedByStatus && !!violation.status) ||
                          isOnApproval ||
                          watchOnly ||
                          !!violation.fixes?.length ||
                          isDisabledByAgreement
                        }
                        onChange={onChangeFixPlanDate}
                        minDate={spacetime().toNativeDate()}
                      />
                    </FieldBlock>
                  </Row>
                </>
              ) : (
                <ErknmDecisionViolationFixPlanDatesField
                  fieldName={fieldName}
                  formRef={formRef}
                  violation={violation}
                  watchOnly={watchOnly || isDisabledByAgreement}
                />
              )}
            </Row>
            <Row margin="none">
              <ErknmDecisionViolationFixPlanMethodsField
                fieldName={fieldName}
                formRef={formRef}
                violation={violation}
                watchOnly={watchOnly || isDisabledByAgreement}
                isPaperless={isPaperless}
                mId={mId}
                isKnm={isKnm}
                isPm={isPm}
                isDecision={isDecision}
                isResultDecisionSinged={isResultDecisionSinged}
                extractVersion={extractVersion}
              />
            </Row>
            <Row margin="none">
              <ErknmDecisionViolationFixPlanExplanationsField
                fieldName={fieldName}
                formRef={formRef}
                violation={violation}
                watchOnly={watchOnly || isDisabledByAgreement}
                isPaperless={isPaperless}
                mId={mId}
                isKnm={isKnm}
                isPm={isPm}
                isDecision={isDecision}
                isResultDecisionSinged={isResultDecisionSinged}
                extractVersion={extractVersion}
              />
            </Row>
            {((isPaperless && violation.isFixed?.value) || isFixedByStatus) && (
              <>
                <Row>
                  <FieldBlock
                    label="Фактическая дата устранения"
                    placementLabel="left"
                    labelFontSize="small"
                    type="not-space"
                    color={isPaperless ? 'black' : undefined}
                    className={cn({ [styles.FieldBlock]: isPaperless })}
                  >
                    <DatePickerField
                      className={isPaperless ? undefined : 'shared-row-field'}
                      name={`${fieldName}.fixFactDate`}
                      disabled
                    />
                  </FieldBlock>
                </Row>
                {!hasMobileAppInfo && (
                  <Row>
                    <FieldBlock
                      label={getTitleFieldWithRedStar({
                        label: `Фактический способ устранения нарушения`,
                        field: `${fieldName}.fixFactMethod`,
                        isPm,
                        isDecision,
                        isKnm: !isPm && !isDecision,
                      })}
                      placementLabel="left"
                      labelFontSize="small"
                      type="not-space"
                      color={isPaperless ? 'black' : undefined}
                      className={cn({ [styles.FieldBlock]: isPaperless })}
                      id={`${mId}.fixFactMethod`}
                    >
                      <SelectInputFieldAutoOptions
                        className={isPaperless ? undefined : 'shared-row-field'}
                        id={`${fieldName}.fixFactMethod`}
                        name={`${fieldName}.fixFactMethod`}
                        optionName={CommonCatalogsNamesEnum.VIOLATION_FIX_METHOD}
                        transformFunc={getFilterFixFactMethodSelectOptions}
                        isClearable={false}
                        disabled={
                          isPaperless && violation.isFixed?.value
                            ? fixFactMethodInitialValue || isDisabledByAgreement
                            : !isFixedByStatus || fixFactMethodInitialValue || isDisabledByAgreement
                        }
                      />
                    </FieldBlock>
                  </Row>
                )}

                {!isRepeatCheck && (violation.repeatCorrected?.value || violation.repeatCorrectedInEvent?.value) && (
                  <Row>
                    <FieldBlock
                      label={`Ссылка на мероприятие, в ходе которого устранено нарушение`}
                      placementLabel="left"
                      labelFontSize="small"
                      type="not-space"
                      color={'black'}
                      className={cn(styles.FieldBlock, styles.FieldBlockCenter)}
                    >
                      <a
                        className={styles.LinkTargetPassport}
                        href={violation.targetLink}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {violation.targetErpId}
                      </a>
                    </FieldBlock>
                  </Row>
                )}
              </>
            )}
            {hasViolationFixItems && (
              <ErknmDecisionViolationFixItems
                violation={violation}
                formRef={formRef}
                name={fieldName}
                isFixedFieldName={isFixedFieldName}
                watchOnly={watchOnly}
                isDisabledByAgreement={isDisabledByAgreement}
                isPaperless={isPaperless}
                id={mId}
                isKnm={isKnm}
                isPm={isPm}
                isDecision={isDecision}
                isSupervisor={isSupervisor}
                isFixed={
                  violation.isFixed?.value ||
                  violation.repeatCorrected?.value ||
                  violation.repeatCorrectedInEvent?.value
                }
                isExpandAll={isExpandAll}
                isRepeatCheck={isRepeatCheck}
              />
            )}
          </>
        )}
      </>
    </KnmCollapse>
  );
};
